import { useEffect, useState, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
// @mui
import Fab from '@mui/material/Fab';
import Paper from '@mui/material/Paper';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import Badge from '@mui/material/Badge';
import List from '@mui/material/List';
import CircularProgress from '@mui/material/CircularProgress';
import Avatar from '@mui/material/Avatar';
import TextField from '@mui/material/TextField';
import InputAdornment from '@mui/material/InputAdornment';
import Divider from '@mui/material/Divider';
import Button from '@mui/material/Button';
// redux
import { useDispatch, useSelector } from 'src/redux/store';
import {
  getConversations,
  getConversation,
  setActiveConversation,
  resetActiveConversation,
  subscribeToMessages,
  unsubscribeFromMessages,
  sendMessage,
} from 'src/redux/slices/chat';
// hooks
import { useAuthContext } from 'src/auth/hooks';
// routes
import { paths } from 'src/routes/paths';
// sections
import ChatMessageList from 'src/sections/chat/chat-message-list';
import ChatMessageInput from 'src/sections/chat/chat-message-input';
import { useGetNavItem } from 'src/sections/chat/hooks';
// components
import Iconify from 'src/components/iconify';
// api
import axios, { API_ENDPOINTS } from 'src/utils/axios';

// ----------------------------------------------------------------------

const PANEL_WIDTH = 360;
const PANEL_HEIGHT = 520;

export default function FloatingChatWidget() {
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { user } = useAuthContext();
  const currentUserId = user?.id;

  const { conversations, conversationsStatus, currentConversationId } = useSelector(
    (state) => state.chat
  );

  const [listOpen, setListOpen] = useState(false);
  const [search, setSearch] = useState('');

  // Yurme agent panel state — separate from the human chat flow.
  const [agentPanelOpen, setAgentPanelOpen] = useState(false);
  const [agentSessions, setAgentSessions] = useState([]);
  const [agentLoading, setAgentLoading] = useState(false);

  // Hide on the full chat page or the dedicated agent page — no point
  // duplicating the UI on either.
  const onFullChatPage = location.pathname.startsWith('/dashboard/chat');
  const onAgentPage = location.pathname.startsWith('/dashboard/agent');

  // Fetch conversations when the list is opened for the first time
  useEffect(() => {
    if (listOpen && !conversationsStatus.loading && conversations.allIds.length === 0) {
      dispatch(getConversations());
    }
  }, [listOpen, dispatch, conversationsStatus.loading, conversations.allIds.length]);

  // When a chat is open, load its messages and subscribe to realtime updates
  useEffect(() => {
    if (!currentConversationId || !currentUserId) return undefined;

    dispatch(getConversation(currentConversationId));
    dispatch(subscribeToMessages(currentUserId, currentConversationId));

    return () => {
      dispatch(unsubscribeFromMessages());
    };
  }, [currentConversationId, currentUserId, dispatch]);

  const totalUnread = useMemo(
    () =>
      conversations.allIds.reduce(
        (sum, id) => sum + (conversations.byId[id]?.unreadCount || 0),
        0
      ),
    [conversations]
  );

  const filteredConversations = useMemo(() => {
    const items = conversations.allIds
      .map((id) => conversations.byId[id])
      .filter(Boolean);
    if (!search.trim()) return items;
    const q = search.toLowerCase();
    return items.filter((c) => {
      const name =
        c.groupName ||
        c.participants?.find((p) => String(p.id) !== String(currentUserId))?.name ||
        '';
      return name.toLowerCase().includes(q);
    });
  }, [conversations, search, currentUserId]);

  const currentConversation =
    currentConversationId &&
    (conversations.byId[currentConversationId] ||
      conversations.byId[String(currentConversationId)]);

  const participantsInConversation = useMemo(() => {
    if (!currentConversation?.participants) return [];
    return currentConversation.participants.filter(
      (p) => String(p.id) !== String(currentUserId)
    );
  }, [currentConversation, currentUserId]);

  const handleToggleList = () => setListOpen((v) => !v);

  const handleCloseAll = () => {
    setListOpen(false);
    setAgentPanelOpen(false);
    dispatch(resetActiveConversation());
  };

  // Click on the pinned "Yurme assistant" entry. Fetch the user's agent
  // sessions: if there are none, jump straight to /dashboard/agent (the
  // page autostarts an onboarding session). Otherwise, show the session
  // list in a side-panel — clicking a row navigates to that session.
  const handleOpenAgent = async () => {
    setAgentPanelOpen(true);
    setAgentLoading(true);
    try {
      const res = await axios.get(API_ENDPOINTS.agent.sessions, {
        params: { status: 'active', limit: 50 },
      });
      const list = res.data?.data || [];
      setAgentSessions(list);
      if (list.length === 0) {
        // No sessions yet — go to the dedicated page so the autostart
        // endpoint can spawn the onboarding session and the user lands
        // straight in the conversation.
        setAgentPanelOpen(false);
        setListOpen(false);
        navigate(paths.dashboard.agent.root);
      }
    } catch (_) {
      setAgentSessions([]);
    } finally {
      setAgentLoading(false);
    }
  };

  const handleCloseAgent = () => {
    setAgentPanelOpen(false);
  };

  const handlePickAgentSession = (session) => {
    setAgentPanelOpen(false);
    setListOpen(false);
    navigate(paths.dashboard.agent.session(session.id));
  };

  const handleNewAgentChat = () => {
    setAgentPanelOpen(false);
    setListOpen(false);
    // /dashboard/agent will autostart a new session if none is open, or
    // resume the active onboarding one — either way the user lands in
    // a chat ready to type.
    navigate(paths.dashboard.agent.root);
  };

  const handleOpenChat = (conversation) => {
    // For 1-to-1, use the other participant's id as the conversation key (matches onClickNavItem logic)
    let targetId = conversation.id;
    if (conversation.type !== 'GROUP') {
      const other = conversation.participants?.find(
        (p) => String(p.id) !== String(currentUserId)
      );
      if (other) targetId = other.id;
    }
    dispatch(setActiveConversation(targetId));
  };

  const handleCloseChat = () => {
    dispatch(resetActiveConversation());
  };

  const handleExpandList = () => {
    setListOpen(false);
    navigate('/dashboard/chat');
  };

  const handleExpandChat = () => {
    if (currentConversationId) {
      navigate(`/dashboard/chat/${currentConversationId}`);
    } else {
      navigate('/dashboard/chat');
    }
  };

  if (onFullChatPage || onAgentPage || !currentUserId) return null;

  const anyOpen = listOpen || !!currentConversationId || agentPanelOpen;

  return (
    <>
      {/* Floating button — hidden while anything is open to keep things tidy */}
      {!anyOpen && (
        <Badge
          color="error"
          badgeContent={totalUnread}
          max={99}
          overlap="circular"
          sx={{
            position: 'fixed',
            right: 24,
            bottom: 24,
            zIndex: (theme) => theme.zIndex.modal + 1,
          }}
        >
          <Fab color="primary" aria-label="Open chat" onClick={handleToggleList}>
            <Iconify icon="solar:chat-round-dots-bold" width={26} />
          </Fab>
        </Badge>
      )}

      {/* Chats list popup — rightmost */}
      {listOpen && (
        <Paper
          elevation={12}
          sx={{
            position: 'fixed',
            right: 24,
            bottom: 24,
            width: PANEL_WIDTH,
            height: PANEL_HEIGHT,
            maxWidth: 'calc(100vw - 48px)',
            maxHeight: 'calc(100vh - 48px)',
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
            borderRadius: 2,
            zIndex: (theme) => theme.zIndex.modal + 1,
          }}
        >
          <ListView
            totalUnread={totalUnread}
            loading={conversationsStatus.loading}
            conversations={filteredConversations}
            currentUserId={currentUserId}
            activeChatId={currentConversationId}
            search={search}
            onSearchChange={setSearch}
            onPickConversation={handleOpenChat}
            onPickAgent={handleOpenAgent}
            agentActive={agentPanelOpen}
            onExpand={handleExpandList}
            onClose={handleCloseAll}
          />
        </Paper>
      )}

      {/* Yurme assistant sessions panel — positioned to the LEFT of the
          list (same slot as a chat-detail popup would use). */}
      {agentPanelOpen && (
        <Paper
          elevation={12}
          sx={{
            position: 'fixed',
            right: listOpen ? 24 + PANEL_WIDTH + 16 : 24,
            bottom: 24,
            width: PANEL_WIDTH,
            height: PANEL_HEIGHT,
            maxWidth: 'calc(100vw - 48px)',
            maxHeight: 'calc(100vh - 48px)',
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
            borderRadius: 2,
            zIndex: (theme) => theme.zIndex.modal + 1,
          }}
        >
          <AgentSessionsPanel
            loading={agentLoading}
            sessions={agentSessions}
            onPickSession={handlePickAgentSession}
            onNewChat={handleNewAgentChat}
            onClose={handleCloseAgent}
          />
        </Paper>
      )}

      {/* Chat popup — positioned to the LEFT of the list (or at the right edge if list is closed) */}
      {currentConversationId && (
        <Paper
          elevation={12}
          sx={{
            position: 'fixed',
            right: listOpen ? 24 + PANEL_WIDTH + 16 : 24,
            bottom: 24,
            width: PANEL_WIDTH,
            height: PANEL_HEIGHT,
            maxWidth: 'calc(100vw - 48px)',
            maxHeight: 'calc(100vh - 48px)',
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
            borderRadius: 2,
            zIndex: (theme) => theme.zIndex.modal + 1,
          }}
        >
          <ChatView
            conversation={currentConversation}
            participants={participantsInConversation}
            currentUserId={currentUserId}
            currentConversationId={currentConversationId}
            onExpand={handleExpandChat}
            onClose={handleCloseChat}
          />
        </Paper>
      )}
    </>
  );
}

// ----------------------------------------------------------------------

function ListView({
  totalUnread,
  loading,
  conversations,
  currentUserId,
  activeChatId,
  search,
  onSearchChange,
  onPickConversation,
  onPickAgent,
  agentActive,
  onExpand,
  onClose,
}) {
  return (
    <>
      <Stack
        direction="row"
        alignItems="center"
        sx={{ px: 2, py: 1.5, borderBottom: (t) => `1px solid ${t.palette.divider}` }}
      >
        <Typography variant="subtitle1" sx={{ flex: 1 }}>
          Chats {totalUnread > 0 && `(${totalUnread})`}
        </Typography>
        <IconButton size="small" onClick={onExpand} title="Open full screen">
          <Iconify icon="solar:maximize-square-line-duotone" width={18} />
        </IconButton>
        <IconButton size="small" onClick={onClose} title="Close">
          <Iconify icon="mingcute:close-line" width={18} />
        </IconButton>
      </Stack>

      <Box sx={{ px: 2, py: 1 }}>
        <TextField
          size="small"
          fullWidth
          placeholder="Search chats..."
          value={search}
          onChange={(e) => onSearchChange(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Iconify icon="eva:search-fill" width={18} />
              </InputAdornment>
            ),
          }}
        />
      </Box>

      <Divider />

      <Box sx={{ flex: 1, overflowY: 'auto' }}>
        {/* Pinned Yurme assistant entry — always at the top, regardless
            of search query or human-chat list state. */}
        <List disablePadding>
          <AgentNavItem selected={agentActive} onClick={onPickAgent} />
          <Divider />
        </List>

        {loading && conversations.length === 0 ? (
          <Stack alignItems="center" justifyContent="center" sx={{ py: 6 }}>
            <CircularProgress size={24} />
          </Stack>
        ) : conversations.length === 0 ? (
          <Stack alignItems="center" justifyContent="center" sx={{ py: 6, px: 2 }}>
            <Typography variant="body2" color="text.secondary">
              No chats yet
            </Typography>
          </Stack>
        ) : (
          <List disablePadding>
            {conversations.map((c) => {
              // Active if the conversation's key (1-to-1: other user id, group: conversation.id) matches activeChatId
              let convKey = c.id;
              if (c.type !== 'GROUP') {
                const other = c.participants?.find((p) => String(p.id) !== String(currentUserId));
                if (other) convKey = other.id;
              }
              const selected = String(convKey) === String(activeChatId);
              return (
                <CompactNavItem
                  key={c.id}
                  conversation={c}
                  currentUserId={currentUserId}
                  selected={selected}
                  onClick={() => onPickConversation(c)}
                />
              );
            })}
          </List>
        )}
      </Box>
    </>
  );
}

// ----------------------------------------------------------------------

function CompactNavItem({ conversation, currentUserId, selected, onClick }) {
  const { isGroup, unreadCount, displayName, displayText, participants } = useGetNavItem({
    conversation,
    currentUserId,
  });

  const single = participants[0] || {};

  return (
    <Box
      onClick={onClick}
      sx={{
        px: 2,
        py: 1.25,
        display: 'flex',
        alignItems: 'center',
        gap: 1.5,
        cursor: 'pointer',
        bgcolor: selected ? 'action.selected' : 'transparent',
        '&:hover': { bgcolor: selected ? 'action.selected' : 'action.hover' },
      }}
    >
      <Avatar
        src={single.avatarUrl}
        alt={single.name || displayName}
        sx={{
          width: 40,
          height: 40,
          bgcolor: isGroup ? 'primary.main' : undefined,
          color: isGroup ? 'primary.contrastText' : undefined,
        }}
      >
        {isGroup && <Iconify icon="solar:users-group-rounded-bold" width={22} />}
      </Avatar>

      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography noWrap variant="subtitle2">
          {displayName}
        </Typography>
        <Typography
          noWrap
          variant="body2"
          color={unreadCount ? 'text.primary' : 'text.secondary'}
          sx={{ fontWeight: unreadCount ? 600 : 400 }}
        >
          {displayText}
        </Typography>
      </Box>

      {!!unreadCount && (
        <Box
          sx={{
            minWidth: 20,
            height: 20,
            px: 0.75,
            borderRadius: 10,
            bgcolor: 'info.main',
            color: 'info.contrastText',
            fontSize: 11,
            fontWeight: 700,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          {unreadCount > 99 ? '99+' : unreadCount}
        </Box>
      )}
    </Box>
  );
}

// ----------------------------------------------------------------------

function ChatView({
  conversation,
  participants,
  currentUserId,
  currentConversationId,
  onExpand,
  onClose,
}) {
  const dispatch = useDispatch();
  const other = participants[0] || {};
  const isGroup = conversation?.type === 'GROUP';
  const title = isGroup ? conversation?.groupName || 'Group' : other.name || 'Chat';
  const messages = conversation?.messages || [];

  const handleSendMessage = (body, messageType = 'text', mediaUrl = null) => {
    if (!currentConversationId) return;
    dispatch(sendMessage(currentConversationId, body, messageType, mediaUrl));
  };

  return (
    <>
      <Stack
        direction="row"
        alignItems="center"
        spacing={1}
        sx={{ px: 1.5, py: 1, borderBottom: (t) => `1px solid ${t.palette.divider}` }}
      >
        <Avatar
          src={other.avatarUrl}
          alt={title}
          sx={{
            width: 32,
            height: 32,
            bgcolor: isGroup ? 'primary.main' : undefined,
            color: isGroup ? 'primary.contrastText' : undefined,
          }}
        >
          {isGroup && <Iconify icon="solar:users-group-rounded-bold" width={18} />}
        </Avatar>
        <Typography variant="subtitle2" noWrap sx={{ flex: 1 }}>
          {title}
        </Typography>
        <IconButton size="small" onClick={onExpand} title="Open full screen">
          <Iconify icon="solar:maximize-square-line-duotone" width={18} />
        </IconButton>
        <IconButton size="small" onClick={onClose} title="Close">
          <Iconify icon="mingcute:close-line" width={18} />
        </IconButton>
      </Stack>

      <Box sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
        <Box sx={{ flex: 1, overflowY: 'auto' }}>
          {messages.length === 0 ? (
            <Stack alignItems="center" justifyContent="center" sx={{ py: 6, px: 2, height: '100%' }}>
              <Typography variant="body2" color="text.secondary">
                No messages yet. Say hi!
              </Typography>
            </Stack>
          ) : (
            <ChatMessageList messages={messages} participants={participants} />
          )}
        </Box>

        <ChatMessageInput
          recipients={[]}
          onSendMessage={handleSendMessage}
          onSendCompose={() => {}}
          currentConversationId={currentConversationId}
        />
      </Box>
    </>
  );
}

// ----------------------------------------------------------------------

// The pinned Yurme-agent row in the chat list. Visually mirrors a
// regular CompactNavItem but uses the agent icon and skips the
// participant lookup since it isn't a human conversation.
function AgentNavItem({ selected, onClick }) {
  return (
    <Box
      onClick={onClick}
      sx={{
        px: 2,
        py: 1.25,
        display: 'flex',
        alignItems: 'center',
        gap: 1.5,
        cursor: 'pointer',
        bgcolor: selected ? 'action.selected' : 'transparent',
        '&:hover': { bgcolor: selected ? 'action.selected' : 'action.hover' },
      }}
    >
      <Avatar
        sx={{
          width: 40,
          height: 40,
          bgcolor: 'primary.main',
          color: 'primary.contrastText',
        }}
      >
        <Iconify icon="solar:atom-bold-duotone" width={22} />
      </Avatar>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Stack direction="row" alignItems="center" spacing={0.75}>
          <Typography noWrap variant="subtitle2">
            Yurme assistant
          </Typography>
          <Box
            sx={{
              fontSize: 9,
              fontWeight: 700,
              letterSpacing: 0.5,
              color: 'primary.main',
              border: '1px solid',
              borderColor: 'primary.main',
              borderRadius: 0.5,
              px: 0.5,
              py: '1px',
              lineHeight: 1.2,
            }}
          >
            AI
          </Box>
        </Stack>
        <Typography noWrap variant="body2" color="text.secondary">
          Ask the agent anything
        </Typography>
      </Box>
      <Iconify icon="eva:arrow-ios-forward-fill" width={18} sx={{ color: 'text.disabled' }} />
    </Box>
  );
}

// ----------------------------------------------------------------------

function fAgentRelativeTime(iso) {
  if (!iso) return '';
  const t = new Date(iso).getTime();
  if (Number.isNaN(t)) return '';
  const diff = Date.now() - t;
  const min = Math.floor(diff / 60_000);
  if (min < 1) return 'just now';
  if (min < 60) return `${min}m ago`;
  const h = Math.floor(min / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.floor(h / 24);
  if (d < 7) return `${d}d ago`;
  return new Date(iso).toLocaleDateString();
}

function AgentSessionsPanel({ loading, sessions, onPickSession, onNewChat, onClose }) {
  return (
    <>
      <Stack
        direction="row"
        alignItems="center"
        spacing={1}
        sx={{ px: 1.5, py: 1, borderBottom: (t) => `1px solid ${t.palette.divider}` }}
      >
        <Avatar
          sx={{ width: 32, height: 32, bgcolor: 'primary.main', color: 'primary.contrastText' }}
        >
          <Iconify icon="solar:atom-bold-duotone" width={18} />
        </Avatar>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography variant="subtitle2" noWrap>
            Yurme assistant
          </Typography>
          <Typography variant="caption" color="text.disabled" noWrap>
            {sessions.length} chat{sessions.length === 1 ? '' : 's'}
          </Typography>
        </Box>
        <IconButton size="small" onClick={onClose} title="Close">
          <Iconify icon="mingcute:close-line" width={18} />
        </IconButton>
      </Stack>

      <Box sx={{ flex: 1, overflowY: 'auto' }}>
        {loading ? (
          <Stack alignItems="center" justifyContent="center" sx={{ py: 6 }}>
            <CircularProgress size={22} />
          </Stack>
        ) : sessions.length === 0 ? (
          <Stack alignItems="center" justifyContent="center" sx={{ py: 6, px: 3, textAlign: 'center' }}>
            <Typography variant="body2" color="text.secondary">
              No agent chats yet.
            </Typography>
          </Stack>
        ) : (
          <List disablePadding>
            {sessions.map((s) => (
              <Box
                key={s.id}
                onClick={() => onPickSession(s)}
                sx={{
                  px: 2,
                  py: 1.25,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1.5,
                  cursor: 'pointer',
                  '&:hover': { bgcolor: 'action.hover' },
                }}
              >
                <Iconify icon="solar:chat-line-bold-duotone" width={18} sx={{ color: 'text.disabled' }} />
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography noWrap variant="subtitle2">
                    {s.title || 'New chat'}
                  </Typography>
                  <Typography noWrap variant="caption" color="text.disabled">
                    {fAgentRelativeTime(s.lastMessageAt || s.created_date)}
                    {s.messageCount != null && ` · ${s.messageCount} message${s.messageCount === 1 ? '' : 's'}`}
                  </Typography>
                </Box>
                <Iconify icon="eva:arrow-ios-forward-fill" width={16} sx={{ color: 'text.disabled' }} />
              </Box>
            ))}
          </List>
        )}
      </Box>

      {/* Footer — quick "new chat" entry that drops the user on the full
          agent page (which autostarts an onboarding session if needed). */}
      <Box sx={{ p: 1.5, borderTop: (t) => `1px solid ${t.palette.divider}` }}>
        <Button
          fullWidth
          variant="contained"
          startIcon={<Iconify icon="mingcute:add-line" width={18} />}
          onClick={onNewChat}
        >
          New chat
        </Button>
      </Box>
    </>
  );
}
